/**
 * Port of `initPortfolioFilter` from the template's main.js.
 *
 * The category chips (`.filter-portfolio .filter-btn[data-filter]`) show only the cards
 * whose `data-category` matches. Clicking the active chip again clears the filter, which
 * is the source's way of offering "all" without a separate chip. Cards fade out, then the
 * surviving ones slide up with a stagger.
 */

const CARD_SELECTOR = [
  '.mg-portfolio-item',
  '.card-portfolio',
  '.card_case__studies',
  '.blog-card',
  '.blog-card-2',
  '.product-card',
].join(', ');

const STAGGER_MS = 60;
const FADE_MS = 400;

export function setupPortfolioFilter(root: HTMLElement) {
  const buttons = Array.from(
    root.querySelectorAll<HTMLElement>('.filter-portfolio .filter-btn')
  );
  const cards = Array.from(root.querySelectorAll<HTMLElement>(CARD_SELECTOR));
  if (!buttons.length || !cards.length) return () => {};

  const timers: number[] = [];
  const clearTimers = () => {
    timers.forEach((id) => window.clearTimeout(id));
    timers.length = 0;
  };

  const apply = (filter: string) => {
    clearTimers();
    const visible: HTMLElement[] = [];

    cards.forEach((card) => {
      const category = card.dataset.category;
      if (filter === 'all' || category === filter) {
        visible.push(card);
        return;
      }
      card.style.transition = `opacity 0.3s ease, transform 0.3s ease`;
      card.style.opacity = '0';
      card.style.display = 'none';
      card.style.transform = '';
    });

    visible.forEach((card, index) => {
      card.style.display = '';
      card.style.opacity = '0';
      card.style.transform = 'translateY(24px)';
      // Read back so the browser commits the start state before the transition runs.
      void card.offsetHeight;
      const id = window.setTimeout(() => {
        card.style.transition = `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`;
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * STAGGER_MS);
      timers.push(id);
    });
  };

  const onClick = (event: Event) => {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const wasActive = button.classList.contains('active');

    buttons.forEach((other) => other.classList.remove('active'));
    if (!wasActive) button.classList.add('active');

    apply(wasActive ? 'all' : (button.dataset.filter ?? 'all'));
  };

  buttons.forEach((button) => button.addEventListener('click', onClick));

  return () => {
    clearTimers();
    buttons.forEach((button) => {
      button.removeEventListener('click', onClick);
      button.classList.remove('active');
    });
    cards.forEach((card) => {
      card.style.removeProperty('display');
      card.style.removeProperty('opacity');
      card.style.removeProperty('transform');
      card.style.removeProperty('transition');
    });
  };
}
